import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  TextInput,
  Select,
  SelectItem,
  SelectItemGroup,
  SelectSkeleton,
  TextArea,
  Link
} from 'carbon-components-react';
import { FormError } from 'components';
import { ArrowRight16 } from '@carbon/icons-react';
import { connect, useSelector } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import { Undo16 } from '@carbon/icons-react';
import Utils from 'utilities/helper.util';
import { isString, orderBy } from 'lodash';
import { selectOrgListByBoundary } from 'redux/members';

const mapStateToProps = ({ app }: RootState) => ({
  app
});
const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector>;

const SelectAssetsOwnersForm: React.FC<Props | any> = (props: any) => {
  const { app, values } = props;
  const isLoading = app?.loading;
  const orgListByBoundary: any = useSelector(selectOrgListByBoundary);
  const [isAuthorityOtherName, setIsAuthorityOtherName] = useState<boolean>(
    false
  );

  const [dropDownOptions, setDropDownOptions] = useState<any | undefined>(
    undefined
  );

  const [localAuthority, setLocalAuthority] = useState<any>([]);
  const [utility, setUtility] = useState<any>([]);
  const [selectedAuthorityValue, setSelectedAuthorityValue] = useState<string>(
    'default-value'
  );

  const getAuthorityValue = useCallback(
    (value: string) => {
      if (value === 'private' || value === 'other') {
        return `${value},More`;
      }
      const org = orgListByBoundary.find(
        (item: any) => item.id.toString() === value
      );
      if (org) {
        if (org.organisationType === 'LOCAL GOVERNMENT') {
          return `${value},Local Authority`;
        } else {
          return `${value},Utility`;
        }
      }

      return 'default-value';
    },
    [orgListByBoundary]
  );

  useEffect(() => {
    const _localAuthority: any = [];
    const _utility: any = [];
    orgListByBoundary.map((item: any) => {
      const { id, name, organisationType } = item;
      if (organisationType === 'LOCAL GOVERNMENT') {
        return _localAuthority.push({ value: id, label: name });
      } else {
        return _utility.push({ value: id, label: name });
      }
    });
    setLocalAuthority(orderBy(_localAuthority, ['label'], ['asc']));
    setUtility(orderBy(_utility, ['label'], ['asc']));
  }, [orgListByBoundary]);

  // Grouped drowndown box
  useEffect(() => {
    const others: { value: string | null; label: string }[] = [
      {
        label: 'Private',
        value: 'private'
      },
      {
        label: 'Other',
        value: 'other'
      }
    ];

    const authorityNameItems = [
      { 'Local Authority': localAuthority },
      { Utility: orderBy(utility, ['label'], ['asc']) },
      { More: others }
    ];

    if (authorityNameItems && authorityNameItems.length > 0) {
      const gKeys: any = authorityNameItems.map(
        (item: any) => Object.keys(item)[0]
      );
      const gValues: any = authorityNameItems.map(
        (item: any) => Object.values(item)[0]
      );
      const DDOptionsDom = gValues.map((options: any, key: number) => (
        <SelectItemGroup key={key} label={gKeys[key]}>
          {options.map((item: any) => (
            <SelectItem
              key={item.value}
              text={item.label}
              value={[item.value, gKeys[key]]}
            />
          ))}
        </SelectItemGroup>
      ));
      setDropDownOptions(DDOptionsDom);
    }
  }, [localAuthority, utility]);

  const {
    touched: fTouched,
    errors: fErrors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    updateValues,
    goToPreviousFormHandler
  } = props;
  const inputError = ((errors: any, touched: any) => (
    key: 'userReference' | 'notes' | 'authorityId' | 'otherAuthorityName'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );

  useEffect(() => {
    updateValues(values);
    if (values?.authorityId) {
      setSelectedAuthorityValue(getAuthorityValue(values.authorityId));
      values.authorityId === 'other' && setIsAuthorityOtherName(true);
    }
  }, [values, updateValues, getAuthorityValue, isAuthorityOtherName]);

  const fieldsChangeHandler = (obj: any) => {
    const { key, data } = obj;
    if (key === 'authorityId') {
      const _data = data.split(',');
      values.authorityId = isString(_data[0])
        ? _data[0].toLowerCase()
        : _data[0];
      const selectedAuthId = getAuthorityValue(values.authorityId);
      setSelectedAuthorityValue(selectedAuthId);
      //Group option value
      values.authorityId === 'other'
        ? setIsAuthorityOtherName(true)
        : setIsAuthorityOtherName(false);
    }

    updateValues(values);
  };

  values.otherAuthorityName =
    values.authorityId === 'private' ? '' : values.otherAuthorityName;
  return (
    <form
      onSubmit={handleSubmit}
      className="lodgeEnquiryFrom custom-scroller"
      noValidate
    >
      <div className="lodgeEnquiryFrom--body">
        <section className="formFieldItem">
          {dropDownOptions && dropDownOptions.length > 0 ? (
            <Select
              className="groupedDropDown"
              value={selectedAuthorityValue}
              helperText=""
              id="authorityId"
              invalidText=""
              labelText="Working on behalf of"
              disabled={
                dropDownOptions.length === 0 ||
                Utils.isObjectEmpty(dropDownOptions)
              }
              onChange={(e: any) =>
                fieldsChangeHandler({
                  key: 'authorityId',
                  data: e.target.value
                })
              }
            >
              <SelectItem
                text="Choose an option"
                disabled={true}
                value="default-value"
              />
              {dropDownOptions}
            </Select>
          ) : (
            <SelectSkeleton />
          )}
          {inputError('authorityId')}
          {isAuthorityOtherName && (
            <div className="otherAuthorityName--field">
              <TextInput
                id="otherAuthorityName"
                name="otherAuthorityName"
                labelText="Other (name)"
                data-testid="otherAuthorityName"
                onChange={handleChange}
                onBlur={handleBlur}
                defaultValue={values.otherAuthorityName}
              />

              {inputError('otherAuthorityName')}
            </div>
          )}
        </section>
        <section className="formFieldItem">
          <TextInput
            id="userReference"
            name="userReference"
            labelText="Your reference (optional)"
            placeholder="Brief reference to help identify this job"
            data-testid="userReference"
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={50}
            defaultValue={values.userReference}
          />
          {inputError('userReference')}
        </section>

        <section className="formFieldItem">
          <TextArea
            helperText="500 characters count maximum"
            id="notes"
            name="notes"
            labelText="Notes (optional)"
            onChange={handleChange}
            placeholder="Share any special information about your job with asset owners"
            data-testid="notes"
            maxLength={500}
            defaultValue={values.notes}
          />
          {inputError('notes')}
        </section>
      </div>
      <div className="progressBar--wrapper">
        <div className="progressBar completed"></div>
        <div className="progressBar completed"></div>
        <div className="progressBar completed"></div>
      </div>
      <section className="button--wrapper">
        <Button
          className="cancelEnquiryButton"
          kind="secondary"
          onClick={goToPreviousFormHandler}
          renderIcon={Undo16}
          iconDescription="Previous"
          hasIconOnly
          tooltipPosition="bottom"
          tooltipAlignment="start"
        />
        <Button
          className="submitForm"
          type="submit"
          kind={!fErrors ? 'danger' : 'primary'}
          renderIcon={ArrowRight16}
          disabled={
            selectedAuthorityValue === 'default-value' ||
            (values.authorityId === 'other' &&
              values.otherAuthorityName === '') ||
            isSubmitting ||
            isLoading
          }
          data-testid="submit"
        >
          {'Lodge enquiry'}
        </Button>
        <div className="recaptcha-disclaimer">
          This site is protected by reCAPTCHA and the Google{' '}
          <Link href="https://policies.google.com/privacy" target="_blank">
            Privacy Policy{' '}
          </Link>{' '}
          and{' '}
          <Link href="https://policies.google.com/terms" target="_blank">
            Terms of Service
          </Link>{' '}
          apply.
        </div>
      </section>
    </form>
  );
};

SelectAssetsOwnersForm.displayName = 'SelectAssetsOwnersForm';

export default connector(SelectAssetsOwnersForm);
